/**
 * AccountPickerDialog — modal used when the user clicks "Marcar pagado" /
 * "Marcar cobrado" on a payment/receivable that has no `account_id` set.
 *
 * Why a dedicated dialog instead of silently using the primary account?
 * Freelancers often have multiple wallets (USD wallet for client income,
 * COP debit card for daily spend, BTC wallet for savings) and which one
 * funds a given payment / receives a cobro carries real semantic weight.
 * The previous "fallback to primary" behaviour silently moved cash on the
 * wrong account, producing untraceable balance drift. This dialog forces
 * the explicit choice — and the chosen account is persisted on the source
 * doc, so future edits / re-deletes can audit it.
 *
 * For ``mode="pay"`` the picker also offers DEBT funding (paying via TC /
 * credit card) — selecting a debt INCREASES that debt's balance on
 * mark_paid. Receivables only ever land on cash accounts.
 *
 * Parent controls open via `target` (null = closed). Parent owns submit.
 *
 * onSubmit signature: ``(payload: {account_id?, debt_id?}) => Promise<void>``
 */
import { useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { formatMoney } from "../lib/api";
import { useFxConvert, useProfile, useDebts } from "../hooks/queries";
import { Wallet, CreditCard, AlertTriangle } from "lucide-react";

/** Pick the "primary" account the way the backend does it: prefer the
 *  first bank account, fall back to the first available account. Keeps
 *  the default consistent with adjust_primary_account on the server. */
const pickPrimary = (accounts) => {
  if (!accounts || accounts.length === 0) return "";
  const bank = accounts.find((a) => a.type === "bank");
  return (bank || accounts[0]).id;
};

export function AccountPickerDialog({
  target,        // null | the payment/receivable that triggered the picker
  mode,          // "pay" | "collect"
  cashAccounts,  // available accounts
  onSubmit,      // ({account_id?, debt_id?}) => Promise<void>
  onClose,
  lang,
}) {
  const isEs = lang === "es";
  const [tab, setTab] = useState("account");      // "account" | "debt"
  const [selected, setSelected] = useState("");
  const [selectedDebt, setSelectedDebt] = useState("");
  const [busy, setBusy] = useState(false);
  const convert = useFxConvert();
  const { data: profile } = useProfile();
  const profileCcy = profile?.currency || "USD";
  const { data: debts = [] } = useDebts();
  const [preview, setPreview] = useState(null);  // {amount, currency} | null

  // Default to the PRIMARY account whenever the picker (re-)opens.
  useEffect(() => {
    if (target && cashAccounts.length) {
      setSelected((prev) => prev || pickPrimary(cashAccounts));
    }
    if (!target) {
      setSelected("");
      setSelectedDebt("");
      setTab("account");
      setPreview(null);
    }
  }, [target, cashAccounts]);

  // Live preview: convert the target's amount into the SELECTED account's
  // currency. Skip the work for same-currency rows and for the debt-funded
  // path (debt mode shows a different message — the debt grows in its own ccy).
  useEffect(() => {
    let cancelled = false;
    if (tab !== "account") { setPreview(null); return; }
    const run = async () => {
      if (!target || !selected) { setPreview(null); return; }
      const acct = cashAccounts.find((a) => a.id === selected);
      if (!acct) { setPreview(null); return; }
      const acctCcy = (acct.currency || profileCcy).toUpperCase();
      const srcCcy = (target.original_currency || profileCcy).toUpperCase();
      const srcAmount = target.original_currency
        ? Number(target.original_amount || 0)
        : Number(target.amount || 0);
      if (acctCcy === srcCcy) {
        if (!cancelled) setPreview({ amount: srcAmount, currency: acctCcy, fx: false });
        return;
      }
      try {
        const converted = await convert(srcAmount, srcCcy, acctCcy);
        if (!cancelled && converted != null) {
          setPreview({ amount: converted, currency: acctCcy, fx: true, srcCcy, srcAmount });
        }
      } catch { if (!cancelled) setPreview(null); }
    };
    run();
    return () => { cancelled = true; };
  }, [target, selected, cashAccounts, convert, profileCcy, tab]);

  const submit = async () => {
    if (tab === "account" && !selected) return;
    if (tab === "debt" && !selectedDebt) return;
    setBusy(true);
    try {
      await onSubmit(tab === "debt" ? { debt_id: selectedDebt } : { account_id: selected });
    } finally {
      setBusy(false);
    }
  };

  const isPay = mode === "pay";
  const action = isPay
    ? (isEs ? "Confirmar pago" : "Confirm payment")
    : (isEs ? "Confirmar cobro" : "Confirm collection");

  const fxHint = useMemo(() => {
    if (!target) return null;
    if (!target.original_currency || !target.original_amount) return null;
    return `${formatMoney(target.original_amount, target.original_currency)} ${target.original_currency}`;
  }, [target]);

  const selectedDebtObj = debts.find((d) => d.id === selectedDebt);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="account-picker-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEs ? "¿Cómo se va a pagar?" : "How will this be paid?"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {target && (
              <>
                {isPay
                  ? (isEs ? "Esta cuota no tiene cuenta asignada. " : "This payment has no funding source. ")
                  : (isEs ? "Este cobro no tiene cuenta destino. " : "This receivable has no destination account. ")}
                <span className="font-semibold">{target.concept || target.client_name}</span>
                {fxHint && <span className="text-slate-500"> ({fxHint})</span>}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Funding-source tabs. Only `pay` mode shows the Debt option —
            receivables can only land in cash accounts. */}
        {isPay && (
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 self-start">
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              data-testid="account-picker-tab-account"
              onClick={() => setTab("account")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                tab === "account"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Wallet className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              {isEs ? "Cuenta" : "Account"}
            </button>
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              data-testid="account-picker-tab-debt"
              onClick={() => setTab("debt")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                tab === "debt"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <CreditCard className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              {isEs ? "Deuda / TC" : "Debt / Card"}
            </button>
          </div>
        )}

        <div className="space-y-3 py-2">
          {tab === "account" ? (
            <>
              <div>
                <Label className="text-xs">{isEs ? "Cuenta" : "Account"}</Label>
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger data-testid="account-picker-select">
                    <SelectValue placeholder={isEs ? "Selecciona…" : "Choose…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {cashAccounts.length === 0 && (
                      <SelectItem value="_empty" disabled>
                        {isEs ? "No tienes cuentas. Creá una en /Cuentas." : "No accounts. Create one in /Accounts."}
                      </SelectItem>
                    )}
                    {cashAccounts.map((a) => (
                      <SelectItem
                        key={a.id}
                        value={a.id}
                        data-testid={`account-picker-option-${a.id}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span>{a.name}</span>
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">{a.currency}</span>
                          <span className="text-[10px] text-slate-500 tabular-nums">· {formatMoney(a.balance || 0, a.currency)}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {preview && (
                <div
                  data-testid="account-picker-preview"
                  className={`rounded-lg border px-3 py-2 text-xs flex items-baseline justify-between gap-3 ${
                    isPay
                      ? "bg-red-50 border-red-200 text-red-800"
                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                  }`}
                >
                  <span className="text-[11px] uppercase tracking-wider font-semibold">
                    {isPay ? (isEs ? "se restará" : "will debit") : (isEs ? "se sumará" : "will credit")}
                  </span>
                  <span className="font-display font-bold tabular-nums text-sm">
                    {isPay ? "−" : "+"}{formatMoney(preview.amount, preview.currency)}
                    {preview.fx && (
                      <span className="ml-1 text-[10px] text-slate-500 font-normal">
                        ≈ {formatMoney(preview.srcAmount, preview.srcCcy)} {preview.srcCcy}
                      </span>
                    )}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">{isEs ? "Deuda / TC" : "Debt / Card"}</Label>
                <Select value={selectedDebt} onValueChange={setSelectedDebt}>
                  <SelectTrigger data-testid="account-picker-debt-select">
                    <SelectValue placeholder={isEs ? "Elige la deuda…" : "Choose a debt…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {debts.length === 0 && (
                      <SelectItem value="_empty" disabled>
                        {isEs ? "No tienes deudas. Creá una en /Deudas." : "No debts. Create one in /Debts."}
                      </SelectItem>
                    )}
                    {debts.map((d) => (
                      <SelectItem
                        key={d.id}
                        value={d.id}
                        data-testid={`account-picker-debt-option-${d.id}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span>{d.name}</span>
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">{d.currency}</span>
                          <span className="text-[10px] text-slate-500 tabular-nums">
                            · {isEs ? "saldo" : "balance"} {formatMoney(d.balance || 0, d.currency)}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedDebtObj && (
                <div
                  data-testid="account-picker-debt-preview"
                  className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs"
                >
                  <div className="flex items-center gap-1.5 font-semibold mb-0.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {isEs ? "Esta deuda AUMENTARÁ" : "This debt's balance will INCREASE"}
                  </div>
                  <div className="tabular-nums">
                    {selectedDebtObj.name}: {formatMoney(selectedDebtObj.balance || 0, selectedDebtObj.currency)}
                    <span className="text-amber-700"> + {fxHint || formatMoney(target?.amount || 0, profileCcy)}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {isEs ? "Cancelar" : "Cancel"}
          </Button>
          <Button
            data-testid="account-picker-confirm"
            onClick={submit}
            disabled={
              busy ||
              (tab === "account" && (!selected || selected === "_empty")) ||
              (tab === "debt" && (!selectedDebt || selectedDebt === "_empty"))
            }
            className={isPay ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
          >
            {action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
